import moment from 'moment'
import { array, func, object, oneOf, oneOfType, string } from 'prop-types'
import { PureComponent } from 'react'
import { AnalyticsConsumer, AnalyticsLayer } from '@opallabs/quanta'
import UnmanagedMonth from './unmanaged'

/**
 * Converts all date-like objects to Moments.
 *
 * @param {import('.').DateOrMoment} [date] A Date or Moment instance.
 * @returns {moment.Moment} A Moment instance of the given date, or the
 *  current date if `date` wasn't valid.
 */
export const wrapMoment = (date) =>
  date instanceof moment ? date : moment(moment.isDate(date) ? date : undefined)

/**
 * When allowed dates are limited, this function returns a reasonable
 * date to start the calendar on.
 *
 * @param {import('.').SelectableDates} [allowableDates]
 * @returns {moment.Moment}
 */
export const getInitialDate = (allowableDates) => {
  if (allowableDates) {
    if (Array.isArray(allowableDates)) {
      return moment.min(allowableDates.map(wrapMoment))
    } else if (typeof allowableDates === 'object') {
      if (allowableDates.start && !allowableDates.end) {
        return moment.max(moment(), wrapMoment(allowableDates.start))
      } else if (!allowableDates.start && allowableDates.end) {
        return moment.min(moment(), wrapMoment(allowableDates.end))
      }
    }
  }

  return moment()
}

/**
 * Validates if the given moment instance is an allowed date.
 *
 * @param {import('.').SelectableDates} [allowableDates]
 * @param {moment.Moment} date
 * @returns {boolean}
 */
export const isAllowableDate = (allowableDates, date) => {
  if (allowableDates) {
    if (Array.isArray(allowableDates))
      return allowableDates.some((value) => value.isSame(date, 'day'))
    if (typeof allowableDates === 'object') {
      if (allowableDates.start && allowableDates.end) {
        return date.isBetween(
          allowableDates.start,
          allowableDates.end,
          'day',
          '[]'
        )
      }
      if (allowableDates.start)
        return date.isSameOrAfter(allowableDates.start, 'day')
      if (allowableDates.end)
        return date.isSameOrBefore(allowableDates.end, 'day')
    }
    return false
  }
  return true
}

export default class Month extends PureComponent {
  static propTypes = {
    allowableDates: oneOfType([object, array]),
    classPrefix: string,
    highlighted: oneOfType([object, array]),
    onDaySelect: func,
    onViewDateChange: func,
    startOfWeek: oneOf([0, 1, 2, 3, 4, 5, 6]),
    theme: oneOf(['dark', 'light']),
    value: object,
  }

  static defaultProps = {
    startOfWeek: 0,
    theme: 'light',
  }

  constructor(props) {
    super(props)

    this.state = {
      viewDate: props.value,
    }
  }

  onDaySelect = (date, track) => {
    if (
      this.props.onDaySelect &&
      isAllowableDate(this.props.allowableDates, date)
    ) {
      this.setState(() => ({
        date,
      }))
      this.props.onDaySelect(date)
      track('Date Selected', { value: date._d })
    }
  }

  onViewDateChange = (viewDate, track) => {
    const dateAsMoment = moment(viewDate)

    this.setState(() => ({
      viewDate: dateAsMoment,
    }))
    if (this.props.onViewDateChange) {
      this.props.onViewDateChange(dateAsMoment)
    }
    track('Date View Changed', { value: viewDate })
  }

  render() {
    const {
      classPrefix,
      highlighted = [],
      allowableDates,
      startOfWeek,
      theme,
    } = this.props
    const { date, viewDate } = this.state
    const { onDaySelect, onViewDateChange } = this

    return (
      <AnalyticsLayer name="Date Picker">
        <AnalyticsConsumer>
          {({ track }) => (
            <UnmanagedMonth
              allowable={isAllowableDate.bind(date, allowableDates)}
              highlighted={[].concat(highlighted)}
              classPrefix={classPrefix}
              date={date}
              viewDate={viewDate ?? getInitialDate(allowableDates)}
              onDaySelect={(e) => onDaySelect(e, track)}
              startOfWeek={startOfWeek}
              onViewDateChange={(e) => onViewDateChange(e, track)}
              theme={theme}
            />
          )}
        </AnalyticsConsumer>
      </AnalyticsLayer>
    )
  }
}
Month.displayName = 'Kuiper: Month ← DatePicker ← Inputs'
